<template>
  <div class="c-new-and-recurring-business-settings">
    <div class="flex justify-between items-center">
      <div class="max-w-[480px]">
        <p class="text-neutral-text-strong font-bold mb-4">
          Opportunity Type Field
        </p>
        <p class="text-sm text-neutral-text">
          Define what field in your CRM captures whether the opportunity is new
          or recurring business
        </p>
      </div>
      <BittsLoading
        :is-loading="!isDisabled && (loading || loadingOpptyFieldOptions)"
        loading-container-class="relative right-[88px]"
        :with-transition="false"
      >
        <div>
          <BittsTooltip>
            <BittsSelect
              v-model="selectedOpportunityFieldId"
              :options="opportunityFieldOptions"
              class="w-[240px]"
              :disabled="isDisabled"
              placeholder="Opportunity Type"
              data-testid="opportunity-field-select"
            />
            <template #title v-if="disabled">
              Enable this CRM to map your opportunity and product fields
            </template>
          </BittsTooltip>
        </div>
      </BittsLoading>
    </div>
    <div
      v-if="operandOptions.length && selectedOpportunityFieldId"
      class="border-l-0 md:border-l border-neutral-border pl-0 md:pl-24 ml-24 mt-24"
    >
      <div class="border border-neutral-border rounded-lg p-12">
        <BusinessSettingsFilterGroup
          v-model="selectedOperandsLookup.newBusiness"
          title="New Business"
          :operand-options="newBusinessOptions"
          :is-multi-select
          :is-disabled="isDisabled"
          class="mb-16"
        />
        <BusinessSettingsFilterGroup
          v-model="selectedOperandsLookup.existingBusiness"
          title="Existing Business"
          :operand-options="recurringBusinessOptions"
          :is-multi-select
          :is-disabled="isDisabled"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { BittsLoading, BittsSelect, BittsTooltip } from '@crossbeam/bitts';

import { ref, toRef, watch } from 'vue';
import { useRoute } from 'vue-router';

import useBusinessSettings from '@/composables/useBusinessSettings';

import BusinessSettingsFilterGroup from './BusinessSettingsFilterGroup.vue';

type Props = {
  isDisabled?: boolean;
};
const { isDisabled } = defineProps<Props>();

const emit = defineEmits<(e: 'settings-changed', changed: boolean) => void>();

const route = useRoute();
const {
  selectedFeedId,
  selectedOperandsLookup,
  selectedOpportunityFieldId,
  opportunityFieldOptions,
  dealSourceFromSelectedFeed,
  operandOptions,
  newBusinessOptions,
  recurringBusinessOptions,
  isMultiSelect,
  isValidSettings,
  loading,
  haveSettingsChanged,
  disabled,
  snapshot,
  resetAll,
  resetOperands,
  fetchFilterableSources,
  setOperandOptions,
  buildPayload, // just for exposing to tests
  saveSettings,
} = useBusinessSettings(toRef(() => isDisabled));

const loadingOpptyFieldOptions = ref(false);
const isFirstLoad = ref(true);

/* root input - if this is reset we should hydrate everything */
watch(
  [selectedFeedId, selectedOpportunityFieldId],
  async ([newFeedId, newFieldId], [oldFeedId, oldFieldId]) => {
    // Skip loading state if disabled - no need to show loading when select is disabled
    if (disabled.value) return;

    // When the feed id changes, update opportunityFieldOptions
    loadingOpptyFieldOptions.value = true;

    if (
      (newFeedId !== oldFeedId || isFirstLoad.value) &&
      dealSourceFromSelectedFeed.value?.id
    ) {
      if (oldFeedId && !isFirstLoad.value) resetAll();
      opportunityFieldOptions.value = await fetchFilterableSources(
        dealSourceFromSelectedFeed.value.id,
      );
    }

    // Only proceed if newFieldId is set
    if (newFieldId && opportunityFieldOptions.value) {
      if (newFieldId !== oldFieldId && oldFieldId !== null) {
        resetOperands();
      }
      await setOperandOptions(newFieldId);
    }
    isFirstLoad.value = false;
    loadingOpptyFieldOptions.value = false;
  },
);

watch(haveSettingsChanged, (dirty) => {
  if (!loading.value) {
    emit('settings-changed', dirty);
  }
});

watch(
  () => isDisabled,
  (disabled) => {
    if (disabled && selectedFeedId.value === Number(route.params?.id)) {
      selectedOpportunityFieldId.value = null;
    } else if (
      selectedFeedId.value === Number(route.params?.id) &&
      snapshot.value?.fieldId
    ) {
      selectedOpportunityFieldId.value = snapshot.value?.fieldId ?? null;
    }
  },
);

defineExpose({
  buildPayload,
  saveSettings,
  isValidSettings,
});
</script>
